import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IClasseSocial,
  IContato,
  IEstadoCivil,
  IFiltrosProjeto,
  IGeolocalizacao,
  IListaCEP,
  IListaCidades,
  IListaDeduplicacao,
  ILocalizacao,
  IOcupacao,
  IProjeto,
  IProjetoTable,
  IRelatorioProjeto,
  IScoreVeles,
  ISexo,
  ISituacaoCadastral,
} from "../../../models";
import { IFormError, IFormProps } from "../../utils";
import { IEscolaridade } from "../../../models/geo-veles/IEscolaridade";
import { IListaCbo } from "../../../models/geo-veles/IListaCbo";

interface ICreateProjetoFormFields {
  nome: string;
  idCliente: number;
  idUsuario: number;
}

interface IEditProjetoFormFields {
  idVelesGeoListaCidade?: number;
  idVelesGeoProjeto: number;
  nome: string;
  ativo: boolean;
}

interface ICreateExtracaoFormFields {
  nome: string;
  quantidade: number;
}

interface IDuplicateProjetoFormFields {
  idVelesGeoProjeto: number;
  nome: string;
  idUsuario: number;
}

export type IActiveSection = "filtros" | "dados";

const createProjetoFormFieldsInitialState: ICreateProjetoFormFields = {
  nome: "",
  idCliente: 0,
  idUsuario: 0,
};

const editProjetoFormFieldsInitialState: IEditProjetoFormFields = {
  idVelesGeoProjeto: 0,
  nome: "",
  ativo: false,
};

const createExtracaoFormFieldsInitialState: ICreateExtracaoFormFields = {
  nome: "",
  quantidade: 0,
};

const duplicateProjetoFormFieldsInitialState: IDuplicateProjetoFormFields = {
  idVelesGeoProjeto: 0,
  nome: "",
  idUsuario: 0,
};

export type IActionTypes = "atualizar" | "exportar";

interface IProjetosState {
  projetos: IProjetoTable[];
  projetoAberto?: IProjeto;
  idVelesGeoListaCidade?: number;
  projetoListFiltros: {
    searchTerm?: string;
    inclusaoStart?: Date;
    inclusaoEnd?: Date;
    ativo?: boolean;
    usuario?: string;
    loginUsuario?: string;
    loginCliente?: string;
  };
  openExportType: boolean;
  action: IActionTypes;
  activeSection: IActiveSection;
  activeDetailsSection: number;
  filtroAtivo: number;
  filtrosSelecionados?: IFiltrosProjeto | undefined;
  projetosTotal: number;
  relatorioProjeto?: IRelatorioProjeto;
  projetosPagination: {
    page: number;
    limit: number;
  };
  listasDeduplicacao?: IListaDeduplicacao[];
  listasCEP?: IListaCEP[];
  listasCidades?: IListaCidades[];
  listaCBO?: IListaCbo[];
  createProjetoForm: IFormProps<ICreateProjetoFormFields> & { open: boolean };
  editProjetoForm: IFormProps<IEditProjetoFormFields> & { open: boolean };
  createExtracaoForm: IFormProps<ICreateExtracaoFormFields>;
  duplicateProjetoForm: IFormProps<IDuplicateProjetoFormFields>;
  openDuplicateProjetoForm: boolean;
}

const initialState: IProjetosState = {
  projetos: [],
  action: "atualizar",
  activeSection: "filtros",
  activeDetailsSection: 0,
  openExportType: false,
  filtroAtivo: 0,
  projetosTotal: 0,
  relatorioProjeto: {
    agregacoes: {
      pessoasComTelefone: 0,
      pessoasSemTelefone: 0,
      pessoasComCelular: 0,
      pessoasSemCelular: 0,
      pessoasComFixo: 0,
      pessoasSemFixo: 0,
      pessoasComWhatsApp: 0,
      pessoasSemWhatsApp: 0,
      pessoasComEndereco: 0,
      pessoasSemEndereco: 0,
      pessoasComEmail: 0,
      pessoasSemEmail: 0,
    },
    dados: {
      deduplicados: 0,
      disponiveis: 0,
      exportados: 0,
    },
  },
  filtrosSelecionados: {},
  listaCBO: [],
  projetoAberto: undefined,
  projetosPagination: {
    page: 0,
    limit: 0,
  },
  createProjetoForm: {
    errors: [],
    fields: createProjetoFormFieldsInitialState,
    touched: false,
    delivered: false,
    open: false,
  },
  editProjetoForm: {
    errors: [],
    fields: editProjetoFormFieldsInitialState,
    touched: false,
    delivered: false,
    open: false,
  },
  createExtracaoForm: {
    errors: [],
    fields: createExtracaoFormFieldsInitialState,
    touched: false,
    delivered: false,
  },
  projetoListFiltros: {
    searchTerm: undefined,
    inclusaoStart: undefined,
    inclusaoEnd: undefined,
    usuario: undefined,
    loginUsuario: undefined,
    loginCliente: undefined,
    ativo: true,
  },
  duplicateProjetoForm: {
    errors: [],
    fields: duplicateProjetoFormFieldsInitialState,
    touched: false,
    delivered: false,
  },
  openDuplicateProjetoForm: false,
};

export const validateCreateProjetoForm = (
  fields: ICreateProjetoFormFields
): IFormError<keyof ICreateProjetoFormFields>[] => {
  const errors: IFormError<keyof ICreateProjetoFormFields>[] = [];

  if (!fields.nome) {
    errors.push({
      key: "nome",
      message: "Nome é obrigatório",
    });
  } else {
    if (
      fields.nome.length === 0 ||
      fields.nome.replace("  ", " ").trim().length === 0
    ) {
      errors.push({
        key: "nome",
        message: "O nome do projeto não pode ficar em branco",
      });
    }
  }

  return errors;
};

export const validateEditProjetoForm = (
  fields: IEditProjetoFormFields
): IFormError<keyof IEditProjetoFormFields>[] => {
  const errors: IFormError<keyof IEditProjetoFormFields>[] = [];

  if (!fields.nome) {
    errors.push({
      key: "nome",
      message: "Nome é obrigatório",
    });
  } else {
    if (
      fields.nome.length === 0 ||
      fields.nome.replace("  ", " ").trim().length === 0
    ) {
      errors.push({
        key: "nome",
        message: "O nome do projeto não pode ficar em branco",
      });
    }
  }

  return errors;
};

export const validateDuplicateProjetoForm = (
  fields: IDuplicateProjetoFormFields
): IFormError<keyof IDuplicateProjetoFormFields>[] => {
  const errors: IFormError<keyof IDuplicateProjetoFormFields>[] = [];

  if (!fields.nome) {
    errors.push({
      key: "nome",
      message: "Nome é obrigatório",
    });
  } else {
    if (
      fields.nome.length === 0 ||
      fields.nome.replace("  ", " ").trim().length === 0
    ) {
      errors.push({
        key: "nome",
        message: "O nome do projeto não pode ficar em branco",
      });
    }
  }

  return errors;
};

export const validateCreateExtracaoForm = (
  fields: ICreateExtracaoFormFields
): IFormError<keyof ICreateExtracaoFormFields>[] => {
  const errors: IFormError<keyof ICreateExtracaoFormFields>[] = [];

  if (fields.quantidade === 0) {
    errors.push({
      key: "quantidade",
      message: "Insira uma quantidade maior que 0",
    });
  }

  return errors;
};

const projetosSlice = createSlice({
  name: "projetos",
  initialState,
  reducers: {
    // Lists Actions
    setProjetosState(
      state,
      action: PayloadAction<{
        projetos: IProjetoTable[];
        total: number;
      }>
    ) {
      state.projetos = action.payload.projetos;
      state.projetosTotal = action.payload.total;
    },
    setProjetoAbertoState(state, action: PayloadAction<IProjeto>) {
      state.projetoAberto = action.payload;
      if (action.payload.Filtros) {
        // Mapear listaCBO (backend) para listasCbo (frontend)
        const filtros = { ...action.payload.Filtros } as any;
        if (filtros.listaCBO) {
          filtros.listasCbo = filtros.listaCBO;
          delete filtros.listaCBO;
        }

        // Corrige as datas de nascimento se vierem como strings do backend
        if (filtros.dataNascimento) {
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);
          const dataMaximaPermitida = new Date(hoje.getFullYear() - 18, hoje.getMonth(), hoje.getDate());
          dataMaximaPermitida.setHours(23, 59, 59, 999);

          if (filtros.dataNascimento.min) {
            const minDate = new Date(filtros.dataNascimento.min);
            minDate.setHours(0, 0, 0, 0);

            // Se a data mínima é maior que 18 anos atrás, corrige para o máximo permitido
            if (minDate > dataMaximaPermitida) {
              filtros.dataNascimento.min = dataMaximaPermitida;
            } else {
              filtros.dataNascimento.min = minDate;
            }
          }

          if (filtros.dataNascimento.max) {
            const maxDate = new Date(filtros.dataNascimento.max);
            maxDate.setHours(0, 0, 0, 0);
            filtros.dataNascimento.max = maxDate;
          }
        }

        state.filtrosSelecionados = filtros;
      }
    },
    resetProjetoAbertoState(state) {
      state.projetoAberto = initialState.projetoAberto;
      state.filtrosSelecionados = initialState.filtrosSelecionados;
      state.filtroAtivo = initialState.filtroAtivo;
      state.action = initialState.action;
      state.activeSection = initialState.activeSection;
    },
    resetFiltrosSelecionadosState(state) {
      state.filtrosSelecionados = initialState.filtrosSelecionados;
    },
    setProjetosTotalState(state, action: PayloadAction<{ total: number }>) {
      state.projetosTotal = action.payload.total;
    },
    setProjetosPaginationState(
      state,
      action: PayloadAction<{ page: number; limit: number }>
    ) {
      state.projetosPagination = {
        page: action.payload.page,
        limit: action.payload.limit,
      };
    },

    resetRelatorioProjetoState(state) {
      state.relatorioProjeto = initialState.relatorioProjeto;
    },
    // Create Projeto Actions
    setCreateProjetoFormNomeState(
      state,
      action: PayloadAction<{ nome: string }>
    ) {
      state.createProjetoForm.fields.nome = action.payload.nome;
    },
    setCreateProjetoFormIdClienteState(
      state,
      action: PayloadAction<{ idCliente: number }>
    ) {
      state.createProjetoForm.fields.idCliente = action.payload.idCliente;
    },
    setCreateProjetoFormIdUsuarioState(
      state,
      action: PayloadAction<{ idUsuario: number }>
    ) {
      state.createProjetoForm.fields.idUsuario = action.payload.idUsuario;
    },
    setCreateProjetoFormTouchedState(
      state,
      action: PayloadAction<{ touched: boolean }>
    ) {
      state.createProjetoForm.touched = action.payload.touched;
    },
    setCreateProjetoFormErrorsState(
      state,
      action: PayloadAction<{
        errors: IFormError<keyof ICreateProjetoFormFields>[];
      }>
    ) {
      state.createProjetoForm.errors = action.payload.errors;
    },
    setCreateProjetoFormDeliveredState(
      state,
      action: PayloadAction<{ delivered: boolean }>
    ) {
      state.createProjetoForm.delivered = action.payload.delivered;
    },
    setOpenCreateProjetoFormState(state, action: PayloadAction<boolean>) {
      state.createProjetoForm.open = action.payload;
    },
    resetCreateProjetoFormState(state) {
      state.createProjetoForm = {
        fields: createProjetoFormFieldsInitialState,
        errors: [],
        touched: false,
        delivered: false,
        open: false,
      };
    },
    // Edit Projeto Actions
    setEditProjetoFormIdVelesGeoProjetoState(
      state,
      action: PayloadAction<{ idVelesGeoProjeto: number }>
    ) {
      state.editProjetoForm.fields.idVelesGeoProjeto =
        action.payload.idVelesGeoProjeto;
    },
    setEditProjetoFormNomeState(
      state,
      action: PayloadAction<{ nome: string }>
    ) {
      state.editProjetoForm.fields.nome = action.payload.nome;
    },
    setEditProjetoFormAtivoState(
      state,
      action: PayloadAction<{ ativo: boolean }>
    ) {
      state.editProjetoForm.fields.ativo = action.payload.ativo;
    },
    setEditProjetoFormTouchedState(
      state,
      action: PayloadAction<{ touched: boolean }>
    ) {
      state.editProjetoForm.touched = action.payload.touched;
    },
    setEditProjetoFormErrorsState(
      state,
      action: PayloadAction<{
        errors: IFormError<keyof IEditProjetoFormFields>[];
      }>
    ) {
      state.editProjetoForm.errors = action.payload.errors;
    },
    setEditProjetoFormDeliveredState(
      state,
      action: PayloadAction<{ delivered: boolean }>
    ) {
      state.editProjetoForm.delivered = action.payload.delivered;
    },
    setOpenEditProjetoFormState(state, action: PayloadAction<boolean>) {
      state.editProjetoForm.open = action.payload;
    },
    resetEditProjetoFormState(state) {
      state.editProjetoForm = {
        fields: editProjetoFormFieldsInitialState,
        errors: [],
        touched: false,
        delivered: false,
        open: false,
      };
    },
    setProjetoActionState(state, action: PayloadAction<IActionTypes>) {
      state.action = action.payload;
    },
    resetProjetoActionState(state) {
      state.action = initialState.action;
    },
    setProjetoFiltroAtivoState(state, action: PayloadAction<number>) {
      state.filtroAtivo = action.payload;
    },
    resetProjetoFiltroAtivoState(state) {
      state.filtroAtivo = initialState.filtroAtivo;
    },
    setProjetoActiveSectionState(
      state,
      action: PayloadAction<{
        activeSection: IActiveSection;
      }>
    ) {
      state.activeSection = action.payload.activeSection;
    },
    addLocalizacaoFilterState: (
      state,
      action: PayloadAction<{ localizacao: ILocalizacao }>
    ) => {
      const { localizacao } = action.payload;
      const { filtrosSelecionados } = state;

      if (!filtrosSelecionados) return;

      if (filtrosSelecionados.localizacao) {
        const exists = filtrosSelecionados.localizacao.some(
          (loc) =>
            loc.value === localizacao.value &&
            loc.categoria === localizacao.categoria
        );
        if (!exists) {
          filtrosSelecionados.localizacao.push(localizacao);
        }
      } else {
        filtrosSelecionados.localizacao = [localizacao];
      }
    },

    removeLocalizacaoFilterState: (
      state,
      action: PayloadAction<{ localizacao: ILocalizacao }>
    ) => {
      const { localizacao } = action.payload;
      const { filtrosSelecionados } = state;

      if (!filtrosSelecionados || !filtrosSelecionados.localizacao) return;

      const index = filtrosSelecionados.localizacao.findIndex(
        (loc) =>
          loc.value === localizacao.value &&
          loc.categoria === localizacao.categoria
      );

      if (index >= 0) {
        filtrosSelecionados.localizacao.splice(index, 1);
      }

      if (filtrosSelecionados.localizacao.length === 0) {
        filtrosSelecionados.localizacao = undefined;
      }
    },

    addPurgeLocalizacaoFilterState(
      state,
      action: PayloadAction<{
        localizacao: ILocalizacao;
      }>
    ) {
      const { localizacao } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.purgeLocalizacao) {
          const findLocalizacao = filtrosSelecionados.purgeLocalizacao.find(
            (loc) =>
              loc.value === localizacao.value &&
              loc.categoria === localizacao.categoria
          );
          if (!findLocalizacao) {
            filtrosSelecionados.purgeLocalizacao.push(localizacao);
          }
        } else {
          filtrosSelecionados.purgeLocalizacao = [];
          filtrosSelecionados.purgeLocalizacao.push(localizacao);
        }
      }
    },
    removePurgeLocalizacaoFilterState(
      state,
      action: PayloadAction<{
        localizacao: ILocalizacao;
      }>
    ) {
      /* Use SelectedFilter to get the filter index */
      /* Compare value and categoria fields */
      /* Remove from localizacao field of filters */
      const { localizacao } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.purgeLocalizacao) {
          const findLocalizacao = filtrosSelecionados.purgeLocalizacao.find(
            (loc) =>
              loc.value === localizacao.value &&
              loc.categoria === localizacao.categoria
          );
          if (findLocalizacao) {
            const index =
              filtrosSelecionados.purgeLocalizacao.indexOf(findLocalizacao);
            filtrosSelecionados.purgeLocalizacao.splice(index, 1);
          }

          if (filtrosSelecionados.purgeLocalizacao.length === 0) {
            filtrosSelecionados.purgeLocalizacao = undefined;
          }
        }
      }
    },
    setGeolocalizacaoFilterState(
      state,
      action: PayloadAction<{
        geolocalizacao?: {
          data: IGeolocalizacao;
          radius: number;
        };
      }>
    ) {
      if (state.filtrosSelecionados) {
        state.filtrosSelecionados.geolocalizacao =
          action.payload.geolocalizacao;
      }
    },
    setGeolocalizacaoFilterRadiusState(
      state,
      action: PayloadAction<{
        radius: number;
      }>
    ) {
      if (state.filtrosSelecionados) {
        if (state.filtrosSelecionados.geolocalizacao) {
          state.filtrosSelecionados.geolocalizacao.radius =
            action.payload.radius;
        }
      }
    },
    resetGeolocalizacaoFilterState(state) {
      if (state.filtrosSelecionados) {
        state.filtrosSelecionados.geolocalizacao =
          initialState.filtrosSelecionados?.geolocalizacao;
      }
    },
    addCBOFilterState(state, action: PayloadAction<IOcupacao>) {
      const { filtrosSelecionados } = state;

      if (filtrosSelecionados) {
        if (filtrosSelecionados.cbo) {
          const findLocalizacao = filtrosSelecionados.cbo.find(
            (cbo) => cbo.cbocodigo === action.payload.cbocodigo
          );
          if (!findLocalizacao) {
            filtrosSelecionados.cbo.push(action.payload);
          }
        } else {
          filtrosSelecionados.cbo = [];
          filtrosSelecionados.cbo.push(action.payload);
        }
      }
    },
    removeCBOFilterState(state, action: PayloadAction<IOcupacao>) {
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.cbo) {
          const findCBO = filtrosSelecionados.cbo.find(
            (cbo) => cbo.cbocodigo === action.payload.cbocodigo
          );
          if (findCBO) {
            const index = filtrosSelecionados.cbo.indexOf(findCBO);
            filtrosSelecionados.cbo.splice(index, 1);
          }

          if (filtrosSelecionados.cbo.length === 0) {
            filtrosSelecionados.cbo = undefined;
          }
        }
      }
    },
    addProfissaoFilterState(state, action: PayloadAction<IOcupacao>) {
      const { filtrosSelecionados } = state;

      if (filtrosSelecionados) {
        if (filtrosSelecionados.profissao) {
          const findProfissao = filtrosSelecionados.profissao.find(
            (profissao) =>
              profissao.familiacodigo === action.payload.familiacodigo
          );
          if (!findProfissao) {
            filtrosSelecionados.profissao.push(action.payload);
          }
        } else {
          filtrosSelecionados.profissao = [];
          filtrosSelecionados.profissao.push(action.payload);
        }
      }
    },
    removeProfissaoFilterState(state, action: PayloadAction<IOcupacao>) {
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.profissao) {
          const findProfissao = filtrosSelecionados.profissao.find(
            (profissao) =>
              profissao.familiacodigo === action.payload.familiacodigo
          );
          if (findProfissao) {
            const index = filtrosSelecionados.profissao.indexOf(findProfissao);
            filtrosSelecionados.profissao.splice(index, 1);
          }

          if (filtrosSelecionados.profissao.length === 0) {
            filtrosSelecionados.profissao = undefined;
          }
        }
      }
    },
    addEscolaridadeFilterState(
      state,
      action: PayloadAction<{
        escolaridade: IEscolaridade;
      }>
    ) {
      const { escolaridade } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.escolaridade) {
          const findEscolaridade = filtrosSelecionados.escolaridade.find(
            (esc) => esc === escolaridade
          );
          if (!findEscolaridade) {
            filtrosSelecionados.escolaridade.push(escolaridade);
          }
        } else {
          filtrosSelecionados.escolaridade = [];
          filtrosSelecionados.escolaridade.push(escolaridade);
        }
      }
    },
    removeEscolaridadeFilterState(
      state,
      action: PayloadAction<{
        escolaridade: IEscolaridade;
      }>
    ) {
      const { escolaridade } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.escolaridade) {
          const findEscolaridade = filtrosSelecionados.escolaridade.find(
            (esc) => esc === escolaridade
          );
          if (findEscolaridade) {
            const index =
              filtrosSelecionados.escolaridade.indexOf(findEscolaridade);
            filtrosSelecionados.escolaridade.splice(index, 1);
          }

          if (filtrosSelecionados.escolaridade.length === 0) {
            filtrosSelecionados.escolaridade = undefined;
          }
        }
      }
    },
    addListaCEPFilterState(
      state,
      action: PayloadAction<{
        idVelesGeoListaCEP: number;
      }>
    ) {
      const { idVelesGeoListaCEP } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.listasCEP) {
          const findListaCEP = filtrosSelecionados.listasCEP.find(
            (listaCEP) => listaCEP === idVelesGeoListaCEP
          );
          if (!findListaCEP) {
            filtrosSelecionados.listasCEP.push(idVelesGeoListaCEP);
          }
        } else {
          filtrosSelecionados.listasCEP = [];
          filtrosSelecionados.listasCEP.push(idVelesGeoListaCEP);
        }
      }
    },
    removeListaCEPFilterState(
      state,
      action: PayloadAction<{
        idVelesGeoListaCEP: number;
      }>
    ) {
      const { idVelesGeoListaCEP } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.listasCEP) {
          const findListaCEP = filtrosSelecionados.listasCEP.find(
            (listaCEP) => listaCEP === idVelesGeoListaCEP
          );
          if (findListaCEP) {
            const index = filtrosSelecionados.listasCEP.indexOf(findListaCEP);
            filtrosSelecionados.listasCEP.splice(index, 1);
          }

          if (filtrosSelecionados.listasCEP.length === 0) {
            filtrosSelecionados.listasCEP = undefined;
          }
        }
      }
    },
    addPurgeListaCEPFilterState(
      state,
      action: PayloadAction<{
        idVelesGeoListaCEP: number;
      }>
    ) {
      const { idVelesGeoListaCEP } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.purgeListasCEP) {
          const findListaCEPs = filtrosSelecionados.purgeListasCEP.find(
            (cepList) => cepList === idVelesGeoListaCEP
          );
          if (!findListaCEPs) {
            filtrosSelecionados.purgeListasCEP.push(idVelesGeoListaCEP);
          }
        } else {
          filtrosSelecionados.purgeListasCEP = [];
          filtrosSelecionados.purgeListasCEP.push(idVelesGeoListaCEP);
        }
      }
    },
    removePurgeListaCEPFilterState(
      state,
      action: PayloadAction<{
        idVelesGeoListaCEP: number;
      }>
    ) {
      const { idVelesGeoListaCEP } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.purgeListasCEP) {
          const findListaCEPs = filtrosSelecionados.purgeListasCEP.find(
            (cepList) => cepList === idVelesGeoListaCEP
          );
          if (findListaCEPs) {
            const index =
              filtrosSelecionados.purgeListasCEP.indexOf(findListaCEPs);
            filtrosSelecionados.purgeListasCEP.splice(index, 1);
          }

          if (filtrosSelecionados.purgeListasCEP.length === 0) {
            filtrosSelecionados.purgeListasCEP = undefined;
          }
        }
      }
    },

    addListaCidadesFilterState: (
      state,
      action: PayloadAction<{ idVelesGeoListaCidade: number }>
    ) => {
      const { idVelesGeoListaCidade } = action.payload;
      const { filtrosSelecionados } = state;

      if (filtrosSelecionados) {
        if (filtrosSelecionados.listasCidades) {
          const findListaCidade = filtrosSelecionados.listasCidades.find(
            (listaCidade) => listaCidade === idVelesGeoListaCidade
          );
          if (!findListaCidade) {
            filtrosSelecionados.listasCidades.push(idVelesGeoListaCidade);
          }
        } else {
          filtrosSelecionados.listasCidades = [idVelesGeoListaCidade];
        }
      }
    },

    removeListaCidadesFilterState: (
      state,
      action: PayloadAction<{ idVelesGeoListaCidade: number }>
    ) => {
      const { idVelesGeoListaCidade } = action.payload;
      const { filtrosSelecionados } = state;

      if (filtrosSelecionados?.listasCidades) {
        const findListaCidade = filtrosSelecionados.listasCidades.find(
          (listaCidade) => listaCidade === idVelesGeoListaCidade
        );

        if (findListaCidade !== undefined) {
          const index =
            filtrosSelecionados.listasCidades.indexOf(findListaCidade);
          filtrosSelecionados.listasCidades.splice(index, 1);
        }

        if (filtrosSelecionados.listasCidades.length === 0) {
          filtrosSelecionados.listasCidades = undefined;
        }
      }
    },

    addPurgeListaCidadesFilterState: (
      state,
      action: PayloadAction<{ idVelesGeoListaCidade: number }>
    ) => {
      if (!state.filtrosSelecionados) return;
      if (!state.filtrosSelecionados.purgeListasCidades) {
        state.filtrosSelecionados.purgeListasCidades = [];
      }
      if(
        !state.filtrosSelecionados.purgeListasCidades.includes(
          action.payload.idVelesGeoListaCidade
        )
      ) {
        state.filtrosSelecionados.purgeListasCidades.push(
          action.payload.idVelesGeoListaCidade
        );
      }
    },

    removePurgeListaCidadesFilterState: (
      state,
      action: PayloadAction<{ idVelesGeoListaCidade: number }>
    ) => {
      if (
        state.filtrosSelecionados &&
        state.filtrosSelecionados.purgeListasCidades
      ) {
        state.filtrosSelecionados.purgeListasCidades =
          state.filtrosSelecionados.purgeListasCidades.filter(
            (id) => id !== action.payload.idVelesGeoListaCidade
          );
      }
    },

    addListaCBOFilterState(
      state,
      action: PayloadAction<{
        idVelesGeoListaCBO: number;
      }>
    ) {
      const { idVelesGeoListaCBO } = action.payload;
      const { filtrosSelecionados } = state;

      if (filtrosSelecionados) {
        if (filtrosSelecionados.listasCbo) {
          const findListaCBO = filtrosSelecionados.listasCbo.find(
            (listaCBO) => listaCBO === idVelesGeoListaCBO
          );
          if (!findListaCBO) {
            filtrosSelecionados.listasCbo.push(idVelesGeoListaCBO);
          }
        } else {
          filtrosSelecionados.listasCbo = [idVelesGeoListaCBO];
        }
      }
    },

    removeListaCBOFilterState(
      state,
      action: PayloadAction<{
        idVelesGeoListaCBO: number;
      }>
    ) {
      const { idVelesGeoListaCBO } = action.payload;
      const { filtrosSelecionados } = state;

      if (filtrosSelecionados && filtrosSelecionados.listasCbo) {
        const index = filtrosSelecionados.listasCbo.findIndex(
          (listaCBO) => listaCBO === idVelesGeoListaCBO
        );

        if (index >= 0) {
          filtrosSelecionados.listasCbo.splice(index, 1);
        }

        if (filtrosSelecionados.listasCbo.length === 0) {
          filtrosSelecionados.listasCbo = undefined;
        }
      }
    },

    addListaDeduplicacaoFilterState(
      state,
      action: PayloadAction<{
        IdVelesGeoListaDeduplicacao: number;
      }>
    ) {
      const { IdVelesGeoListaDeduplicacao } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.listasDeduplicacao) {
          const findListaDeduplicacao =
            filtrosSelecionados.listasDeduplicacao.find(
              (listaDeduplicacao) =>
                listaDeduplicacao === IdVelesGeoListaDeduplicacao
            );
          if (!findListaDeduplicacao) {
            filtrosSelecionados.listasDeduplicacao.push(
              IdVelesGeoListaDeduplicacao
            );
          }
        } else {
          filtrosSelecionados.listasDeduplicacao = [];
          filtrosSelecionados.listasDeduplicacao.push(
            IdVelesGeoListaDeduplicacao
          );
        }
      }
    },
    removeListaDeduplicacaoFilterState(
      state,
      action: PayloadAction<{
        IdVelesGeoListaDeduplicacao: number;
      }>
    ) {
      const { IdVelesGeoListaDeduplicacao } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.listasDeduplicacao) {
          const findListaDeduplicacao =
            filtrosSelecionados.listasDeduplicacao.find(
              (listaDeduplicacao) =>
                listaDeduplicacao === IdVelesGeoListaDeduplicacao
            );
          if (findListaDeduplicacao) {
            const index = filtrosSelecionados.listasDeduplicacao.indexOf(
              findListaDeduplicacao
            );
            filtrosSelecionados.listasDeduplicacao.splice(index, 1);
          }

          if (filtrosSelecionados.listasDeduplicacao.length === 0) {
            filtrosSelecionados.listasDeduplicacao = undefined;
          }
        }
      }
    },
    addSexoFilterState(
      state,
      action: PayloadAction<{
        sexo: ISexo;
      }>
    ) {
      const { sexo } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.sexo) {
          const findSexo = filtrosSelecionados.sexo.find((sx) => sx === sexo);
          if (!findSexo) {
            filtrosSelecionados.sexo.push(sexo);
          }
        } else {
          filtrosSelecionados.sexo = [];
          filtrosSelecionados.sexo.push(sexo);
        }
      }
    },
    removeSexoFilterState(
      state,
      action: PayloadAction<{
        sexo: ISexo;
      }>
    ) {
      const { sexo } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.sexo) {
          const findSexo = filtrosSelecionados.sexo.find((sx) => sx === sexo);
          if (findSexo) {
            const index = filtrosSelecionados.sexo.indexOf(findSexo);
            filtrosSelecionados.sexo.splice(index, 1);
          }

          if (filtrosSelecionados.sexo.length === 0) {
            filtrosSelecionados.sexo = undefined;
          }
        }
      }
    },
    addClasseSocialFilterState(
      state,
      action: PayloadAction<{
        classeSocial: IClasseSocial;
      }>
    ) {
      const { classeSocial } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.classeSocial) {
          const findClasseSocial = filtrosSelecionados.classeSocial.find(
            (classe) => classe === classeSocial
          );
          if (!findClasseSocial) {
            filtrosSelecionados.classeSocial.push(classeSocial);
          }
        } else {
          filtrosSelecionados.classeSocial = [];
          filtrosSelecionados.classeSocial.push(classeSocial);
        }
      }
    },
    removeClasseSocialFilterState(
      state,
      action: PayloadAction<{
        classeSocial: IClasseSocial;
      }>
    ) {
      const { classeSocial } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.classeSocial) {
          const findClasseSocial = filtrosSelecionados.classeSocial.find(
            (classe) => classe === classeSocial
          );
          if (findClasseSocial) {
            const index =
              filtrosSelecionados.classeSocial.indexOf(findClasseSocial);
            filtrosSelecionados.classeSocial.splice(index, 1);
          }

          if (filtrosSelecionados.classeSocial.length === 0) {
            filtrosSelecionados.classeSocial = undefined;
          }
        }
      }
    },
    addSituacaoCadastralFilterState(
      state,
      action: PayloadAction<{
        situacaoCadastral: ISituacaoCadastral;
      }>
    ) {
      const { situacaoCadastral } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.situacaoCadastral) {
          const findSituacaoCadastral =
            filtrosSelecionados.situacaoCadastral.find(
              (score) => score === situacaoCadastral
            );
          if (!findSituacaoCadastral) {
            filtrosSelecionados.situacaoCadastral.push(situacaoCadastral);
          }
        } else {
          filtrosSelecionados.situacaoCadastral = [];
          filtrosSelecionados.situacaoCadastral.push(situacaoCadastral);
        }
      }
    },
    removeSituacaoCadastralFilterState(
      state,
      action: PayloadAction<{
        situacaoCadastral: ISituacaoCadastral;
      }>
    ) {
      const { situacaoCadastral } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.situacaoCadastral) {
          const findSituacaoCadastral =
            filtrosSelecionados.situacaoCadastral.find(
              (score) => score === situacaoCadastral
            );
          if (findSituacaoCadastral) {
            const index = filtrosSelecionados.situacaoCadastral.indexOf(
              findSituacaoCadastral
            );
            filtrosSelecionados.situacaoCadastral.splice(index, 1);
          }

          if (filtrosSelecionados.situacaoCadastral.length === 0) {
            filtrosSelecionados.situacaoCadastral = undefined;
          }
        }
      }
    },
    addScoreVelesFilterState(
      state,
      action: PayloadAction<{
        scoreVeles: IScoreVeles;
      }>
    ) {
      const { scoreVeles } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.scoreVeles) {
          const findScoreVeles = filtrosSelecionados.scoreVeles.find(
            (score) => score === scoreVeles
          );
          if (!findScoreVeles) {
            filtrosSelecionados.scoreVeles.push(scoreVeles);
          }
        } else {
          filtrosSelecionados.scoreVeles = [];
          filtrosSelecionados.scoreVeles.push(scoreVeles);
        }
      }
    },
    removeScoreVelesFilterState(
      state,
      action: PayloadAction<{
        scoreVeles: IScoreVeles;
      }>
    ) {
      const { scoreVeles } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.scoreVeles) {
          const findScoreVeles = filtrosSelecionados.scoreVeles.find(
            (score) => score === scoreVeles
          );
          if (findScoreVeles) {
            const index =
              filtrosSelecionados.scoreVeles.indexOf(findScoreVeles);
            filtrosSelecionados.scoreVeles.splice(index, 1);
          }

          if (filtrosSelecionados.scoreVeles.length === 0) {
            filtrosSelecionados.scoreVeles = undefined;
          }
        }
      }
    },
    addEstadoCivilFilterState(
      state,
      action: PayloadAction<{
        estadoCivil: IEstadoCivil;
      }>
    ) {
      const { estadoCivil } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.estadoCivil) {
          const findEstadoCivil = filtrosSelecionados.estadoCivil.find(
            (estadoCiv) => estadoCiv === estadoCivil
          );
          if (!findEstadoCivil) {
            filtrosSelecionados.estadoCivil.push(estadoCivil);
          }
        } else {
          filtrosSelecionados.estadoCivil = [];
          filtrosSelecionados.estadoCivil.push(estadoCivil);
        }
      }
    },
    removeEstadoCivilFilterState(
      state,
      action: PayloadAction<{
        estadoCivil: IEstadoCivil;
      }>
    ) {
      const { estadoCivil } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.estadoCivil) {
          const findEstadoCivil = filtrosSelecionados.estadoCivil.find(
            (estadoCiv) => estadoCiv === estadoCivil
          );
          if (findEstadoCivil) {
            const index =
              filtrosSelecionados.estadoCivil.indexOf(findEstadoCivil);
            filtrosSelecionados.estadoCivil.splice(index, 1);
          }

          if (filtrosSelecionados.estadoCivil.length === 0) {
            filtrosSelecionados.estadoCivil = undefined;
          }
        }
      }
    },
    setMinRendaMensalFilterState(
      state,
      action: PayloadAction<{
        minRendaMensal?: number;
      }>
    ) {
      const { minRendaMensal } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.rendaPresumidaMensal) {
          filtrosSelecionados.rendaPresumidaMensal.min = minRendaMensal;
        } else {
          filtrosSelecionados.rendaPresumidaMensal = {
            min: minRendaMensal,
            max: undefined,
          };
        }
      }
    },
    setMaxRendaMensalFilterState(
      state,
      action: PayloadAction<{
        maxRendaMensal?: number;
      }>
    ) {
      const { maxRendaMensal } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.rendaPresumidaMensal) {
          filtrosSelecionados.rendaPresumidaMensal.max = maxRendaMensal;
        } else {
          filtrosSelecionados.rendaPresumidaMensal = {
            min: undefined,
            max: maxRendaMensal,
          };
        }
      }
    },
    resetRendaMensalFilterState(state) {
      if (state.filtrosSelecionados) {
        state.filtrosSelecionados.rendaPresumidaMensal =
          initialState.filtrosSelecionados?.rendaPresumidaMensal;
      }
    },
    setMinRendaAnualFilterState(
      state,
      action: PayloadAction<{
        minRendaAnual?: number;
      }>
    ) {
      const { minRendaAnual } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.rendaPresumidaAnual) {
          filtrosSelecionados.rendaPresumidaAnual.min = minRendaAnual;
        } else {
          filtrosSelecionados.rendaPresumidaAnual = {
            min: minRendaAnual,
            max: undefined,
          };
        }
      }
    },
    setMaxRendaAnualFilterState(
      state,
      action: PayloadAction<{
        maxRendaAnual?: number;
      }>
    ) {
      const { maxRendaAnual } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.rendaPresumidaAnual) {
          filtrosSelecionados.rendaPresumidaAnual.max = maxRendaAnual;
        } else {
          filtrosSelecionados.rendaPresumidaAnual = {
            min: undefined,
            max: maxRendaAnual,
          };
        }
      }
    },
    resetRendaAnualFilterState(state) {
      if (state.filtrosSelecionados) {
        state.filtrosSelecionados.rendaPresumidaAnual =
          initialState.filtrosSelecionados?.rendaPresumidaAnual;
      }
    },
    setMinIdadeFilterState(
      state,
      action: PayloadAction<{
        minIdade?: number;
      }>
    ) {
      const { minIdade } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.idade) {
          filtrosSelecionados.idade.min = minIdade;
        } else {
          filtrosSelecionados.idade = {
            min: minIdade,
            max: undefined,
          };
        }
      }
    },
    setMaxIdadeFilterState(
      state,
      action: PayloadAction<{
        maxIdade?: number;
      }>
    ) {
      const { maxIdade } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.idade) {
          filtrosSelecionados.idade.max = maxIdade;
        } else {
          filtrosSelecionados.idade = {
            min: undefined,
            max: maxIdade,
          };
        }
      }
    },
    resetIdadeFilterState(state) {
      if (state.filtrosSelecionados) {
        state.filtrosSelecionados.idade =
          initialState.filtrosSelecionados?.idade;
      }
    },
    setMinDataNascimentoFilterState(
      state,
      action: PayloadAction<{
        minDataNascimento?: Date;
      }>
    ) {
      const { minDataNascimento } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.dataNascimento) {
          filtrosSelecionados.dataNascimento.min = minDataNascimento;
        } else {
          filtrosSelecionados.dataNascimento = {
            max: undefined,
            min: minDataNascimento,
          };
        }
      }
    },
    setMaxDataNascimentoFilterState(
      state,
      action: PayloadAction<{
        maxDataNascimento?: Date;
      }>
    ) {
      const { maxDataNascimento } = action.payload;
      const { filtrosSelecionados } = state;
      if (filtrosSelecionados) {
        if (filtrosSelecionados.dataNascimento) {
          filtrosSelecionados.dataNascimento.max = maxDataNascimento;
        } else {
          filtrosSelecionados.dataNascimento = {
            max: maxDataNascimento,
            min: undefined,
          };
        }
      }
    },
    resetDataNascimentoFilterState(state) {
      if (state.filtrosSelecionados) {
        state.filtrosSelecionados.dataNascimento =
          initialState.filtrosSelecionados?.dataNascimento;
      }
    },
    addContatoFilterState(
      state,
      action: PayloadAction<{
        contato: IContato;
      }>
    ) {
      const { contato } = action.payload;
      const { filtrosSelecionados } = state;

      if (filtrosSelecionados) {
        if (filtrosSelecionados.contato) {
          const findContact = filtrosSelecionados.contato.find(
            (c) => c === contato
          );
          if (!findContact) {
            filtrosSelecionados.contato.push(contato);
          } else {
            filtrosSelecionados.contato = [];
            filtrosSelecionados.contato.push(contato);
          }
        } else {
          filtrosSelecionados.contato = [];
          filtrosSelecionados.contato.push(contato);
        }
      }
    },
    removeContatoFilterState(
      state,
      action: PayloadAction<{
        contato: IContato;
      }>
    ) {
      const { contato } = action.payload;
      const { filtrosSelecionados } = state;

      if (filtrosSelecionados) {
        if (filtrosSelecionados.contato) {
          const findContact = filtrosSelecionados.contato.find(
            (c) => c === contato
          );
          if (findContact) {
            const index = filtrosSelecionados.contato.indexOf(findContact);
            filtrosSelecionados.contato.splice(index, 1);
          }

          if (filtrosSelecionados.contato.length === 0) {
            filtrosSelecionados.contato = undefined;
          }
        }
      }
    },
    setProjetoPageState(
      state,
      action: PayloadAction<{
        page: number;
      }>
    ) {
      state.projetosPagination.page = action.payload.page;
    },
    setProjetoLimitState(
      state,
      action: PayloadAction<{
        limit: number;
      }>
    ) {
      state.projetosPagination.limit = action.payload.limit;
    },
    setRelatorioProjetoState(
      state,
      action: PayloadAction<{
        relatorioProjeto?: IRelatorioProjeto;
      }>
    ) {
      state.relatorioProjeto = action.payload.relatorioProjeto;
    },
    setOpenExportTypeState(state, action: PayloadAction<boolean>) {
      state.openExportType = action.payload;
    },
    // Create Extração Actions
    setCreateExtracaoFormNomeState(
      state,
      action: PayloadAction<{ nome: string }>
    ) {
      state.createExtracaoForm.fields.nome = action.payload.nome;
    },
    setCreateExtracaoFormQuantidadeState(
      state,
      action: PayloadAction<{ quantidade: number }>
    ) {
      state.createExtracaoForm.fields.quantidade = action.payload.quantidade;
    },
    setCreateExtracaoFormTouchedState(
      state,
      action: PayloadAction<{ touched: boolean }>
    ) {
      state.createExtracaoForm.touched = action.payload.touched;
    },
    setCreateExtracaoFormErrorsState(
      state,
      action: PayloadAction<{
        errors: IFormError<keyof ICreateExtracaoFormFields>[];
      }>
    ) {
      state.createExtracaoForm.errors = action.payload.errors;
    },
    setCreateExtracaoFormDeliveredState(
      state,
      action: PayloadAction<{ delivered: boolean }>
    ) {
      state.createExtracaoForm.delivered = action.payload.delivered;
    },
    resetCreateExtracaoFormState(state) {
      state.createExtracaoForm = {
        fields: createExtracaoFormFieldsInitialState,
        errors: [],
        touched: false,
        delivered: false,
      };
    },
    setProjetoListFiltrosSearchTermState(state, action: PayloadAction<string>) {
      state.projetoListFiltros.searchTerm = action.payload;
    },
    resetProjetoListFiltrosSearchTermState(state) {
      state.projetoListFiltros.searchTerm =
        initialState.projetoListFiltros.searchTerm;
    },
    setProjetoListFiltrosInclusaoStartState(
      state,
      action: PayloadAction<Date>
    ) {
      state.projetoListFiltros.inclusaoStart = action.payload;
    },
    resetProjetoListFiltrosInclusaoStartState(state) {
      state.projetoListFiltros.inclusaoStart =
        initialState.projetoListFiltros.inclusaoStart;
    },
    setProjetoListFiltrosInclusaoEndState(state, action: PayloadAction<Date>) {
      state.projetoListFiltros.inclusaoEnd = action.payload;
    },
    resetProjetoListFiltrosInclusaoEndState(state) {
      state.projetoListFiltros.inclusaoEnd =
        initialState.projetoListFiltros.inclusaoEnd;
    },
    setProjetoListFiltrosAtivoState(state, action: PayloadAction<boolean>) {
      state.projetoListFiltros.ativo = action.payload;
    },
    resetProjetoListFiltrosAtivoState(state) {
      state.projetoListFiltros.ativo = initialState.projetoListFiltros.ativo;
    },
    setProjetoListFiltrosUsuarioState(state, action: PayloadAction<string>) {
      state.projetoListFiltros.usuario = action.payload;
    },
    resetProjetoListFiltrosUsuarioState(state) {
      state.projetoListFiltros.usuario =
        initialState.projetoListFiltros.usuario;
    },
    setProjetoListFiltrosLoginUsuarioState(
      state,
      action: PayloadAction<string>
    ) {
      state.projetoListFiltros.loginUsuario = action.payload;
    },
    resetProjetoListFiltrosLoginUsuarioState(state) {
      state.projetoListFiltros.loginUsuario =
        initialState.projetoListFiltros.loginUsuario;
    },

    setProjetoListFiltrosClienteState(state, action: PayloadAction<string>) {
      state.projetoListFiltros.loginCliente = action.payload;
    },
    resetProjetoListFiltrosClienteState(state) {
      state.projetoListFiltros.loginCliente =
        initialState.projetoListFiltros.loginCliente;
    },

    resetProjetoListFiltrosState(state) {
      state.projetoListFiltros = initialState.projetoListFiltros;
    },
    setActiveDetailsSectionState(state, action: PayloadAction<number>) {
      state.activeDetailsSection = action.payload;
    },
    resetCreateExtracaoFormQuantidadeState(state) {
      state.createExtracaoForm.fields.quantidade =
        initialState.createExtracaoForm.fields.quantidade;
    },
    setDuplicateProjetoFormIdVelesGeoProjetoState(
      state,
      action: PayloadAction<{ idVelesGeoProjeto: number }>
    ) {
      state.duplicateProjetoForm.fields.idVelesGeoProjeto =
        action.payload.idVelesGeoProjeto;
    },
    setDuplicateProjetoFormNomeState(
      state,
      action: PayloadAction<{ nome: string }>
    ) {
      state.duplicateProjetoForm.fields.nome = action.payload.nome;
    },
    setDuplicateProjetoFormIdUsuarioState(
      state,
      action: PayloadAction<{ idUsuario: number }>
    ) {
      state.duplicateProjetoForm.fields.idUsuario =
        action.payload.idUsuario;
    },
    setDuplicateProjetoFormErrorsState(
      state,
      action: PayloadAction<{
        errors: IFormError<keyof IDuplicateProjetoFormFields>[];
      }>
    ) {
      state.duplicateProjetoForm.errors = action.payload.errors;
    },
    setDuplicateProjetoFormTouchedState(
      state,
      action: PayloadAction<{ touched: boolean }>
    ) {
      state.duplicateProjetoForm.touched = action.payload.touched;
    },
    resetDuplicateProjetoFormIdVelesGeoProjetoState(state) {
      state.duplicateProjetoForm.fields.idVelesGeoProjeto =
        initialState.duplicateProjetoForm.fields.idVelesGeoProjeto;
    },
    resetDuplicateProjetoFormNomeState(state) {
      state.duplicateProjetoForm.fields.nome =
        initialState.duplicateProjetoForm.fields.nome;
    },
    resetDuplicateProjetoFormIdUsuarioState(state) {
      state.duplicateProjetoForm.fields.idVelesGeoProjeto =
        initialState.duplicateProjetoForm.fields.idVelesGeoProjeto;
    },
    setOpenFormDuplicateProjetoState(state, action: PayloadAction<boolean>) {
      state.openDuplicateProjetoForm = action.payload;
    },
    resetDuplicateProjetoFormState(state) {
      state.duplicateProjetoForm = {
        fields: duplicateProjetoFormFieldsInitialState,
        errors: [],
        touched: false,
        delivered: false,
      };
    },
  },
});

export const {
  //LISTS ACTIONS
  setProjetosState,
  setProjetoAbertoState,
  resetProjetoAbertoState,
  resetFiltrosSelecionadosState,
  setProjetosTotalState,
  setProjetosPaginationState,
  setProjetoLimitState,
  setProjetoPageState,
  setRelatorioProjetoState,
  // CREATE Projeto ACTIONS
  setCreateProjetoFormIdClienteState,
  setCreateProjetoFormIdUsuarioState,
  setCreateProjetoFormNomeState,
  setCreateProjetoFormTouchedState,
  setCreateProjetoFormErrorsState,
  setCreateProjetoFormDeliveredState,
  resetCreateProjetoFormState,
  setOpenCreateProjetoFormState,
  // EDIT Projeto ACTIONS
  setEditProjetoFormAtivoState,
  setEditProjetoFormIdVelesGeoProjetoState,
  setEditProjetoFormNomeState,
  setEditProjetoFormTouchedState,
  setEditProjetoFormErrorsState,
  setEditProjetoFormDeliveredState,
  resetEditProjetoFormState,
  setOpenEditProjetoFormState,
  // Action
  resetProjetoActionState,
  setProjetoActionState,
  // Filtro Ativo
  resetProjetoFiltroAtivoState,
  setProjetoFiltroAtivoState,
  // Filtros Selecionados
  addListaCBOFilterState,
  removeListaCBOFilterState,
  addClasseSocialFilterState,
  addContatoFilterState,
  addEscolaridadeFilterState,
  addEstadoCivilFilterState,
  addListaCEPFilterState,
  addLocalizacaoFilterState,
  addPurgeListaCEPFilterState,
  addPurgeLocalizacaoFilterState,
  addScoreVelesFilterState,
  addSexoFilterState,
  addCBOFilterState,
  addProfissaoFilterState,
  removeCBOFilterState,
  removeProfissaoFilterState,
  removeClasseSocialFilterState,
  removeContatoFilterState,
  removeEscolaridadeFilterState,
  removeEstadoCivilFilterState,
  removeListaCEPFilterState,
  removeLocalizacaoFilterState,
  removePurgeListaCEPFilterState,
  removePurgeLocalizacaoFilterState,
  removeScoreVelesFilterState,
  removeSexoFilterState,
  setGeolocalizacaoFilterRadiusState,
  setGeolocalizacaoFilterState,
  setMaxDataNascimentoFilterState,
  setMaxIdadeFilterState,
  setMaxRendaAnualFilterState,
  setMaxRendaMensalFilterState,
  setMinDataNascimentoFilterState,
  setMinIdadeFilterState,
  setMinRendaAnualFilterState,
  setMinRendaMensalFilterState,
  resetDataNascimentoFilterState,
  resetGeolocalizacaoFilterState,
  resetIdadeFilterState,
  resetRendaAnualFilterState,
  resetRendaMensalFilterState,
  // Seção Ativa
  setProjetoActiveSectionState,
  setOpenExportTypeState,
  // Create Extração
  resetCreateExtracaoFormState,
  setCreateExtracaoFormDeliveredState,
  setCreateExtracaoFormErrorsState,
  setCreateExtracaoFormQuantidadeState,
  setCreateExtracaoFormNomeState,
  setCreateExtracaoFormTouchedState,
  resetProjetoListFiltrosInclusaoEndState,
  resetProjetoListFiltrosInclusaoStartState,
  resetProjetoListFiltrosSearchTermState,
  setProjetoListFiltrosSearchTermState,
  resetProjetoListFiltrosState,
  setProjetoListFiltrosInclusaoEndState,
  setProjetoListFiltrosInclusaoStartState,
  resetProjetoListFiltrosAtivoState,
  setProjetoListFiltrosAtivoState,
  addListaDeduplicacaoFilterState,
  removeListaDeduplicacaoFilterState,
  setActiveDetailsSectionState,
  resetProjetoListFiltrosUsuarioState,
  setProjetoListFiltrosUsuarioState,
  setProjetoListFiltrosLoginUsuarioState,
  resetProjetoListFiltrosLoginUsuarioState,
  setProjetoListFiltrosClienteState,
  resetProjetoListFiltrosClienteState,
  resetRelatorioProjetoState,
  setDuplicateProjetoFormIdVelesGeoProjetoState,
  setDuplicateProjetoFormNomeState,
  setDuplicateProjetoFormIdUsuarioState,
  setDuplicateProjetoFormErrorsState,
  setDuplicateProjetoFormTouchedState,
  resetDuplicateProjetoFormIdVelesGeoProjetoState,
  resetDuplicateProjetoFormNomeState,
  resetDuplicateProjetoFormIdUsuarioState,
  resetDuplicateProjetoFormState,
  setOpenFormDuplicateProjetoState,
  addSituacaoCadastralFilterState,
  removeSituacaoCadastralFilterState,
  addListaCidadesFilterState,
  removeListaCidadesFilterState,
  addPurgeListaCidadesFilterState,
  removePurgeListaCidadesFilterState,
} = projetosSlice.actions;

export const projetosReducer = projetosSlice.reducer;
